import { useMemo } from "react";

import { cn } from "~/core/generic/shadcn-utils";

import { StyledTrimmedText } from "./components/StyledTrimmedText";
import { ALPHABET_LETTERS } from "./constants";
import { KeyProps } from "./interfaces";

/**
 * Key component that represents a single key in the UI.
 *
 * @component
 * @param  props - The props for the Key component.
 * @param  props.type - The type of the key, which determines its styling.
 * @param  props.value - The value to display on the key. For single value keys, select the letter type (e.g., "C" or "K"). If any key is missing from the list of letters, feel free to add ALPHABET_LETTERS in the constants.ts file.
 * @param  props.status - The status of the key, which determines its styling.
 * @param  props.isWhite - Whether the key is white.
 * @example
 * // Example usage of the Key component
 * import { Key } from './Key';
 *
 * const App = () => {
 *   return (
 *     <div>
 *        <Key type="letter" value="C" />
 *        <Key type="text" value="ctrl" />
 *        <Key type="letter" value="K" status="disabled" />
 *        <Key type="letter" value="E" status="active" />
 *        <Key type="letter" value="↑" />
 *        <Key type="letter" value="⌘" />
 *     </div>
 *   );
 * };
 *
 * export default App;
 */

const Key = ({ type, value, status, isWhite }: KeyProps) => {
  const isActive = status === "active";
  const isDisabled = status === "disabled";
  const isDefault = !isActive && !isDisabled;
  const isLetter = type === "letter";
  const isText = type === "text";

  const { keyClass, textClass } = useMemo(
    () => ({
      keyClass: cn(
        `relative !text-[10px] h-layout-spacing-10 min-w-layout-spacing-10 flex items-center justify-center gap-layout-spacing-7
        [&:after]:absolute 
        [&:after]:top-0 
        [&:after]:right-0 
        [&:after]:bottom-0 
        [&:after]:left-0 
        [&:after]:content-[''] 
        [&:after]:bg-core-silver-a3 
        [&:after]:border-core-silver-a5 
        [&:after]:border-[1px] 
        [&:after]:rounded-radius-4`,
        {
          "[&:after]:bg-core-blue-a3 [&:after]:border-core-blue-a4": isActive,
          "[&:after]:bg-core-silver-a2 [&:after]:border-core-silver-a5":
            isDisabled,
          "[&:after]:bg-base-12-a3 [&:after]:border-white-a5":
            isWhite && isDefault,
          "[&:after]:bg-core-blue-a6 [&:after]:border-core-blue-a6":
            isWhite && isActive,
          "[&:after]:bg-base-12-a2 [&:after]:border-white-a5":
            isWhite && isDisabled,
          "px-layout-spacing-4": isText,
          "size-layout-spacing-10": isLetter,
        },
      ),
      textClass: cn(
        "text-center font-normal leading-height-2 tracking-spacing-2 uppercase font-code text-core-silver-a11",
        {
          "text-core-blue-a11": isActive,
          "text-core-silver-a8": isDisabled,
          "text-white-a11": isWhite && isDefault,
          "text-core-blue-a9": isWhite && isActive,
          "text-white-a8": isWhite && isDisabled,
          "font-sans": isLetter,
          "font-code":
            isLetter &&
            ALPHABET_LETTERS.includes(
              value as (typeof ALPHABET_LETTERS)[number],
            ),
        },
      ),
    }),
    [isActive, isDisabled, isWhite, isDefault, isText, isLetter, value],
  );

  return (
    <kbd className={keyClass}>
      <StyledTrimmedText className={textClass}>{value}</StyledTrimmedText>
    </kbd>
  );
};

Key.displayName = "Key";

export default Key;
